import React, { useEffect, useState, useRef } from 'react';
import { Quote } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const EventimePICProfile = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="pic-profile" className="section" ref={sectionRef}>
      <div className="container">
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <h2 
            className={`heading-1 animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              marginBottom: 'var(--spacing-giant)',
              color: 'var(--brand-dark)',
              textAlign: 'center'
            }}
          >
            {t('team.title')}
          </h2>
          
          {/* Team Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 'var(--spacing-xl)',
            justifyItems: 'center',
            maxWidth: '1600px',
            margin: '0 auto',
            width: '90%'
          }}
          className="team-grid"
          >
            
            {/* Team Member 1 - Kahfi Muhammad */}
            <div 
              className={`animated ${isVisible ? 'fadeIn' : ''} team-card`}
              style={{ 
                textAlign: 'center',
                transitionDelay: '0.1s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '280px',
                width: '100%'
              }}
            >
              <div style={{
                width: '160px',
                height: '160px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: '20px',
                boxShadow: 'var(--shadow-heavy)',
                border: '5px solid var(--brand-primary)',
                position: 'relative',
                background: 'var(--bg-subtle)',
                flexShrink: 0
              }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_2789a8f6-76b4-4ebc-887f-34f9e545ce3c/artifacts/06viaa2o_Kahfi%20Muhammad.png"
                  alt="Kahfi Muhammad"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block'
                  }}
                />
              </div>
              <h3 style={{ 
                marginBottom: '10px', 
                color: 'var(--brand-dark)',
                fontSize: '1.25rem',
                fontWeight: '700',
                lineHeight: '1.3'
              }}>
                Kahfi Muhammad
              </h3>
              <p style={{ 
                color: 'var(--brand-primary)', 
                fontWeight: '600',
                fontSize: '1rem',
                lineHeight: '1.4',
                margin: 0
              }}>
                Founder/CEO
              </p>
            </div>

            {/* Team Member 2 - Adrian Taswardi */}
            <div 
              className={`animated ${isVisible ? 'fadeIn' : ''} team-card`}
              style={{ 
                textAlign: 'center',
                transitionDelay: '0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '280px',
                width: '100%'
              }}
            >
              <div style={{
                width: '160px',
                height: '160px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: '20px',
                boxShadow: 'var(--shadow-heavy)',
                border: '5px solid var(--brand-secondary)',
                position: 'relative',
                background: 'var(--bg-subtle)',
                flexShrink: 0
              }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_eventime-landing-1/artifacts/lspud4rd_Adrian%20Taswardi.png"
                  alt="Adrian Taswardi"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block'
                  }}
                />
              </div>
              <h3 style={{ 
                marginBottom: '10px', 
                color: 'var(--brand-dark)',
                fontSize: '1.25rem',
                fontWeight: '700',
                lineHeight: '1.3'
              }}>
                Adrian Taswardi
              </h3>
              <p style={{ 
                color: 'var(--brand-secondary)', 
                fontWeight: '600',
                fontSize: '1rem',
                lineHeight: '1.4',
                margin: 0
              }}>
                Co-Founder/COO
              </p>
            </div>

            {/* Team Member 3 - Chintia */}
            <div 
              className={`animated ${isVisible ? 'fadeIn' : ''} team-card`}
              style={{ 
                textAlign: 'center',
                transitionDelay: '0.3s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '280px',
                width: '100%'
              }}
            >
              <div style={{
                width: '160px',
                height: '160px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: '20px',
                boxShadow: 'var(--shadow-heavy)',
                border: '5px solid var(--brand-dark-alt)',
                position: 'relative',
                background: 'var(--bg-subtle)',
                flexShrink: 0
              }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_eventime-landing-1/artifacts/4461j8qw_chintia.png"
                  alt="Chintia"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block'
                  }}
                />
              </div>
              <h3 style={{ 
                marginBottom: '10px', 
                color: 'var(--brand-dark)',
                fontSize: '1.25rem',
                fontWeight: '700',
                lineHeight: '1.3'
              }}>
                Chintia
              </h3>
              <p style={{ 
                color: 'var(--brand-dark-alt)', 
                fontWeight: '600',
                fontSize: '1rem',
                lineHeight: '1.4',
                margin: 0
              }}>
                Business Development
              </p>
            </div>

            {/* Team Member 4 - Cahya Yoga */}
            <div 
              className={`animated ${isVisible ? 'fadeIn' : ''} team-card`}
              style={{ 
                textAlign: 'center',
                transitionDelay: '0.4s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '280px',
                width: '100%'
              }}
            >
              <div style={{
                width: '160px',
                height: '160px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: '20px',
                boxShadow: 'var(--shadow-heavy)',
                border: '5px solid var(--brand-dark-alt)',
                position: 'relative',
                background: 'var(--bg-subtle)',
                flexShrink: 0
              }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_2789a8f6-76b4-4ebc-887f-34f9e545ce3c/artifacts/marv52yb_Cahya%20Yoga.png"
                  alt="Cahya Yoga"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block'
                  }}
                />
              </div>
              <h3 style={{ 
                marginBottom: '10px', 
                color: 'var(--brand-dark)',
                fontSize: '1.25rem',
                fontWeight: '700',
                lineHeight: '1.3'
              }}>
                Cahya Yoga
              </h3>
              <p style={{ 
                color: 'var(--brand-dark-alt)', 
                fontWeight: '600',
                fontSize: '1rem',
                lineHeight: '1.4',
                margin: 0
              }}>
                Development
              </p>
            </div>

            {/* Team Member 5 - Indri */}
            <div 
              className={`animated ${isVisible ? 'fadeIn' : ''} team-card`}
              style={{ 
                textAlign: 'center',
                transitionDelay: '0.5s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '280px',
                width: '100%'
              }}
            >
              <div style={{
                width: '160px',
                height: '160px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: '20px',
                boxShadow: 'var(--shadow-heavy)',
                border: '5px solid var(--text-light)',
                position: 'relative',
                background: 'var(--bg-subtle)',
                flexShrink: 0
              }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_2789a8f6-76b4-4ebc-887f-34f9e545ce3c/artifacts/algu3z04_Indri.png"
                  alt="Indri"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block'
                  }}
                />
              </div>
              <h3 style={{ 
                marginBottom: '10px', 
                color: 'var(--brand-dark)',
                fontSize: '1.25rem',
                fontWeight: '700',
                lineHeight: '1.3'
              }}>
                Indri Anathasya
              </h3>
              <p style={{ 
                color: 'var(--text-light)', 
                fontWeight: '600',
                fontSize: '1rem',
                lineHeight: '1.4',
                margin: 0
              }}>
                Admin & Marketing
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Team Grid Styles */}
      <style>{`
        @media (max-width: 1400px) {
          .team-grid {
            gap: var(--spacing-large) !important;
          }
        }
        
        @media (max-width: 1200px) {
          .team-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: var(--spacing-xl) !important;
            max-width: 1000px !important;
          }
        }
        
        @media (max-width: 768px) {
          .team-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: var(--spacing-large) !important;
            max-width: 600px !important;
          }
        }
        
        @media (max-width: 480px) {
          .team-grid {
            grid-template-columns: 1fr !important;
            gap: var(--spacing-xl) !important;
            max-width: 400px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default EventimePICProfile;